import api from '@/lib/services/general.service';
import { loginForSave } from "@/lib/services/guest";
import { canCreateAsGuest, addGuestDraft } from '@/lib/services/guestDrafts';
import { toast } from 'vue3-toastify';

const initialState = (important = false, isPreset = false) => ({
  notes: {
    items: [],
    index: 0,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: 0,
    hasNextPage: 0,
  },
  requestData: {
    page: 1,
    pageSize: 50,
    order: 1,
    search: '',
    important: false,
    isPreset: false,
    isDeleted: false,
  },
  presetNotes: {
    items: [],
    index: 0,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: 0,
    hasNextPage: 0,
  },
  totalCount: 0,
  importantCount: 0,
  presetCount: 0,
  binCount: 0,
});
export const state = initialState();
export const getters = {
  isLoading: s => s.isLoading,
  totalCount: s => s.totalCount,
  importantCount: s => s.importantCount,
  presetCount: s => s.presetCount,
  binCount: s => s.binCount,
  pagesLoaded: s => s.pagesLoaded,
  important: s => s.requestData.important,
  isPreset: s => s.requestData.isPreset,
  presetNotes: s => s.presetNotes,
  notes: s => s.notes,
  requestData: s => s.requestData,
};

export const actions = {
  async fetchNotesAndCounts({ dispatch }, newRequestData) {
    await Promise.all([
      dispatch('fetchNotes', newRequestData),
      dispatch('fetchNoteCounts'),
      dispatch('fetchPresets'),
    ]);
  },
  async fetchPresets({ commit, state }) {
    let requestData = {
      page: 1,
      pageSize: 5000,
      order: 1,
      search: '',
      isPreset: true,
    }
    try {
      let response = await api.get('note', requestData);
      if (response) {
        commit("SET_PRESETS", response.data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  },
  async fetchNotes({ commit, state }, newRequestData) {
    let requestData = null;
    if (newRequestData === null) {
      requestData = {
        page: 1,
        pageSize: 50,
        order: 1,
        search: '',
        important: state.requestData.important,
        isPreset: state.requestData.isPreset,
        isDeleted: state.requestData.isDeleted,
      }
    } else {
      requestData = newRequestData;
    }
    commit('SET_REQUEST_DATA', requestData);
    commit('SET_LOADING', true);
    try {
      await fetchNotesWithRetry(commit, requestData, 0);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchNoteCounts({ commit }) {
    try {
      const response = await api.get('note/count', {});
      if (response) {
        commit("SET_COUNT", response.data);
      }
      return true;
    } catch (error) {
      console.error('Error fetching note counts:', error);
      return false;
    }
  },

  // ONE note by id. The SmartBox opens a note for editing from places that never listed notes (a
  // story step), so the loaded page has nothing to read the body from — this reads the note itself.
  // Returns the note, or null when it cannot be read (deleted, foreign, offline).
  async fetchNoteById(_ctx, id) {
    if (!id) return null;
    try {
      const response = await api.getById('note', id);
      return response?.data?.data ?? response?.data ?? null;
    } catch (error) {
      console.error('Error fetching note:', error);
      return null;
    }
  },

  async addNote({ commit, dispatch, state, rootGetters }, note) {
    // Guests keep up to GUEST_LIMIT notes locally; the next one requires signing in.
    if (!rootGetters['user/isAuthenticated']) {
      if (!canCreateAsGuest('note')) { loginForSave(); return new Promise(() => {}) }
      const payload = { ...note }; delete payload.silentToast
      const id = addGuestDraft('note', payload)
      const now = new Date().toISOString()
      const local = { ...payload, id, _local: true, createdDate: now, updateDate: now }
      commit('ADD_NOTE', local)
      return local
    }
    // silentToast: the SmartBox quick-create shows its own in-box feedback instead of a toast.
    const silent = !!note.silentToast
    delete note.silentToast
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'note',
      }
    );
    try {
      const response = await api.insert('note', note);
      if (response) {
        if (note.timelineId) {
          dispatch('timeline/addNoteIntoTimeline', response.data, { root: true });
          //dispatch('sidepanel/addNoteIntoTimeline', response.data, { root: true });
        }
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: 'Note added',
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'note',
            isLoading: false,
          });
          toast.done(toastId);
        });
        dispatch('fetchNotesAndCounts', null);
        // Return the created note (id + timelineId) so callers can attach documents. Truthy, so
        // existing `if (await addNote(...))` checks keep working.
        return response.data;
      }
      return false;
    } catch (error) {
      console.error('Error adding note:', error);
      return false;
    }
  },

  async markAsImportant({ commit, state, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'note',
      }
    );
    const calculatedRequest = calculateForMarkAsImportant(state, request);
    const count = calculatedRequest.ids.length;
    let message = count === 1 ? 'Note sets as important' : `${count} notes sets as important`;
    if (count == 0) {
      message = request.length === 1
        ? 'Note already important'
        : `${request.length} notes already important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'note',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    } else {
      try {
        const response = await api.insert('note/markAsImportant', { ids: calculatedRequest.ids });
        if (response) {
          if (response) {
            response.data.forEach(item => {
              if (item.timelineId) {
                dispatch('timeline/editNoteTimeline', item, { root: true });
                dispatch('sidepanel/editNoteTimeline', item, { root: true });
              }
            });
          }
          commit("UPDATE_NOTES", response.data);
          setTimeout(() => {
            toast.update(toastId, {
              render: message,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              isLoading: false,
              theme: 'note'
            });
            toast.done(toastId);
          });
          dispatch('fetchNoteCounts')
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error marking note as important:', error);
        return false;
      }
    }
  },

  async markAsNotImportant({ commit, state, dispatch }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'note',
      }
    );
    const calculatedRequest = calculateForMarkAsNotImportant(state, request);
    const count = calculatedRequest.ids.length;
    let message = count === 1 ? 'Note sets as not important' : `${count} notes sets as not important`;
    if (count == 0) {
      message = request.length === 1
        ? 'Note already not important'
        : `${request.length} notes already not important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'note',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      try {
        const response = await api.insert('note/markAsNotImportant', { ids: calculatedRequest.ids });
        if (response) {
          response.data.forEach(item => {
            if (item.timelineId) {
              dispatch('timeline/editNoteTimeline', item, { root: true });
              dispatch('sidepanel/editNoteTimeline', item, { root: true });
            }
          });

          commit("UPDATE_NOTES", response.data);
          setTimeout(() => {
            toast.update(toastId, {
              render: message,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'note',
              isLoading: false,
            });
            toast.done(toastId);
          });
          dispatch('fetchNoteCounts')
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error marking note as not important:', error);
        return false;
      }
    }
  },

  async restoreNotes({ dispatch }, ids) {
    const count = ids.length;
    const message = count === 1 ? 'Note restored' : `${count} notes restored`;
    const toastId = toast.loading('Please wait...', { theme: 'note' });
    try {
      const patchDocument = [{ op: 'replace', path: '/isDeleted', value: false }];
      await Promise.all(ids.map(id => api.patch('note', id, patchDocument)));
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'note',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await dispatch('fetchNotesAndCounts', null);
      // Restoring the note un-hides the ongoing it was the last member of.
      dispatch('ongoing/syncAfterEntityChange', null, { root: true });
      return true;
    } catch (error) {
      console.error('Error restoring notes:', error);
      return false;
    }
  },

  async deleteForever({ dispatch }, ids) {
    const count = ids.length;
    const message = count === 1 ? 'Note permanently deleted' : `${count} notes permanently deleted`;
    const toastId = toast.loading('Please wait...', { theme: 'note' });
    try {
      const response = await api.deleteWithRequest('note/deleteForever', { ids });
      if (response.data) {
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'note',
            isLoading: false,
          });
          toast.done(toastId);
        });
        await dispatch('fetchNotesAndCounts', null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error permanently deleting notes:', error);
      return false;
    }
  },

  async deleteNote({ commit, dispatch, state }, request) {
    // `request` may be the bare id array it always was, or `{{ ids, silent }}` (2026-09-15 parity
    // audit B-5/C-9, busy-my-web#466/#474): the universal box reports a delete in its own pink strip
    // with Undo, and the global toast that used to fire alongside it was the second notification the
    // audit reports for one action. Silent is the BOX's choice — every list page still toasts.
    const ids = Array.isArray(request) ? request : (request?.ids || [])
    const silent = !Array.isArray(request) && !!request?.silent
    const count = ids.length
    let message = ''
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'note',
      }
    );
    if (count === 1) {
      message = 'Note deleted'
    } else {
      message = count + ' notes deleted'
    }
    try {
      const response = await api.deleteWithRequest('note/delete', { ids });
      if (response.data) {
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'note',
            isLoading: false,
          });
          toast.done(toastId);
        });
        commit("REMOVE_NOTES", ids);
        // Deleting the last entity out of an ongoing empties (hides) it server-side — re-read the
        // ongoing list so the sidebar drops it without a manual refresh. Restoring brings it back.
        dispatch('ongoing/syncAfterEntityChange', null, { root: true });
        if (ids.length > 1 && state.notes.hasNextPage == true) {
          dispatch('fetchNotesAndCounts', null);
        }
        dispatch('fetchNoteCounts')
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting note:', error);
      return false;
    }
  },
  removePage({ state }, page) {
    state.pagesLoaded.delete(page);
  },

  async editNote({ commit, dispatch }, patchModel) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'note',
      }
    );
    try {
      const response = await api.patch('note', patchModel[0], patchModel[1]);
      if (response) {
        if (response.data.timelineId) {
          dispatch('timeline/editNoteTimeline', response.data, { root: true });
          dispatch('sidepanel/editNoteTimeline', response.data, { root: true });
        }
        commit("UPDATE_NOTE", response.data);
        setTimeout(() => {
          toast.update(toastId, {
            render: "Note updated",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'note',
            isLoading: false,
          });
          toast.done(toastId);
        });
        dispatch('fetchNoteCounts')
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error editing note:', error);
      return false;
    }
  },
};

async function fetchNotesWithRetry(commit, requestData, retryCount) {
  try {
    let response = null;
    let requestParams = { ...requestData };
    if (requestParams.important === false) {
      delete requestParams.important;
    }
    if (requestParams.isPreset === false) {
      delete requestParams.isPreset;
    }
    response = await api.get('note', requestParams);
    if (response) {
      commit("SET_NOTES", response.data);
      commit('SET_LOADING', false);
    }
  } catch (error) {
    console.error('Error fetching notes:', error);
    if (retryCount < 3) {
      await new Promise(resolve => setTimeout(resolve, 10000));
      await fetchNotesWithRetry(commit, requestData, retryCount + 1);
    } else {
      console.error('Max retries reached. Failed to fetch notes.');
    }
  }
}
function calculateForMarkAsImportant(state, request) {
  const notes = state.notes.items.filter(item => request.includes(item.id) && !item.isImportant);
  let ids = notes.map(i => i.id);
  return { ids };
}
function calculateForMarkAsNotImportant(state, request) {
  const notes = state.notes.items.filter(item => request.includes(item.id) && item.isImportant);
  let ids = notes.map(i => i.id);
  return { ids };
}

export const mutations = {
  RESET_STATE(state, important) {
    Object.assign(state, initialState(important));
  },
  SET_PRESETS(s, data) {
    s.presetNotes = data.items;
  },
  SET_NOTES(s, data) {
    s.notes = data;
  },
  SET_LOADING(s, isLoading) {
    s.isLoading = isLoading;
  },
  SET_REQUEST_DATA(s, data) {
    s.requestData.page = data.page
    s.requestData.pageSize = data.pageSize
    s.requestData.order = data.order
    s.requestData.search = data.search
    s.requestData.important = data.important
    s.requestData.isPreset = data.isPreset
    s.requestData.isDeleted = data.isDeleted
  },

  SET_COUNT(s, response) {
    s.totalCount = response.totalCount;
    s.importantCount = response.importantCount;
    s.presetCount = response.presetCount;
    s.binCount = response.binCount;
  },

  ADD_NOTE(s, note) {
    if (note.isPreset) {
      s.presetCount += 1
    }
    if (note.isImportant) {
      s.importantCount += 1
    }
    s.totalCount += 1
    s.notes.items.push(note)
  },
  UPDATE_NOTE(s, response) {
    const note = s.notes.items.find(note => note.id === response.id);
    if (note) {
      note.content = response.content
      note.title = response.title
      note.isImportant = response.isImportant
      note.isPreset = response.isPreset
    }
  },
  UPDATE_NOTES(s, responses) {
    responses.forEach(response => {
      const note = s.notes.items.find(note => note.id === response.id);
      if (note) {
        note.content = response.content
        note.title = response.title
        note.isImportant = response.isImportant
        note.isPreset = response.isPreset
      }
    });
  },
  REMOVE_NOTES(s, ids) {
    s.notes.items = s.notes.items.filter(item => !ids.includes(item.id));
    let totalCount = s.notes.items.length;
    let deletedImportantCount = 0
    s.notes.items = s.notes.items.map(item => {
      if (ids.includes(item.id)) {
        if (item.isImportant) {
          deletedImportantCount++;
        }
        return { ...item, isDeleted: true };
      }
      return item;
    });
    s.notes.totalCount -= ids.length;
    s.binCount += ids.length
    s.totalCount -= ids.length;
    s.notes.totalPages = Math.ceil(totalCount / s.requestData.pageSize);

  },
};

export default {
  state,
  getters,
  actions,
  mutations
};

// Guest mode: addNote itself lets guests keep a couple of notes locally (see guestDrafts.js)
// and prompts sign-in past the limit — so it is NOT blanket-gated behind login here.
